import { CompanyUser } from '@/api';
import { ReactElement, ReactNode, useMemo } from 'react';
import { MyCompanyUsersContext, MyCompanyUsersContextData } from './context';

type MyCompanyUsersProviderProps = {
    readonly myUsers: CompanyUser[];
    readonly children?: ReactNode;
};

/**
 * The authenticated user's own company user in each of their companies, as
 * returned alongside the companies themselves by `GET /v3/companies`. This is
 * what answers every "who am I here?" question — permissions, time settings and
 * the account pages — without holding the whole company roster.
 */
export function MyCompanyUsersProvider({
    myUsers,
    children
}: MyCompanyUsersProviderProps): ReactElement {
    const myUsersByCompanyId = useMemo(() => {
        const map = new Map<string, CompanyUser>();
        // Keyed on companyId, never on position: the server returns one entry
        // per company, but the pairing with `companies` is not positional.
        myUsers.forEach((user) => {
            map.set(user.companyId, user);
        });
        return map;
    }, [myUsers]);

    const value: MyCompanyUsersContextData = useMemo(
        () => ({ myUsers, myUsersByCompanyId }),
        [myUsers, myUsersByCompanyId]
    );

    return (
        <MyCompanyUsersContext.Provider value={value}>
            {children}
        </MyCompanyUsersContext.Provider>
    );
}
